import * as React from 'react';
import { toast } from 'react-toastify';

import { Table, Button, Typography, Flex } from 'antd';

import type IProduct from 'interfaces/IProduct';
import { CHECKOUT_ELEMENTS_URL } from 'settings';
import { BaseOfferTag } from 'v2/components/Shared/BaseOfferTag';
import Truncate from 'v2/components/Shared/TruncateText';
import { useProductOffers } from 'v2/hooks/useProductOffers';
import type { ProductOffer } from 'v2/interfaces/product';

import { Icon } from '@eduzz/ui-antd-theme';

import { ActionsMenu } from '../ActionsMenu';

type ListProps = {
  product: IProduct;
  hasUnsavedChanges: boolean;
  openConfirmModal: () => void;
};

function Component({ product, hasUnsavedChanges, openConfirmModal }: ListProps) {
  const productId = product.id;

  const offersQuery = useProductOffers(
    {
      productId
    },
    { enabled: !!productId }
  );

  function getBaseOffer(offer: ProductOffer) {
    return offer.prices?.find(price => price.currency.code === 'BRL');
  }

  function formatPriceBasedOnBaseOffer(offer: ProductOffer) {
    const baseOffer = getBaseOffer(offer);
    if (!baseOffer) return 'R$ 0,00';
    return baseOffer.value.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    });
  }

  function mountOfferLink(offer: ProductOffer) {
    return `${CHECKOUT_ELEMENTS_URL}/${offer.key}`;
  }

  function copyLinkToClipboard(link: string) {
    navigator.clipboard.writeText(link);
    toast.success('Link copiado para a área de transferência');
  }

  const columns = [
    {
      title: 'Nome da oferta',
      key: 'name',
      render: (_: any, offer: ProductOffer) => (
        <Flex align='center' gap={8} wrap={false}>
          <Typography.Text className='whitespace-nowrap'>
            <Truncate count={20}>{offer.name}</Truncate>
          </Typography.Text>
          {offer.is_default && <BaseOfferTag />}
        </Flex>
      )
    },
    {
      title: 'Valor base',
      key: 'value',
      width: 130,
      render: (_: any, offer: ProductOffer) => formatPriceBasedOnBaseOffer(offer)
    },
    {
      title: 'Preços',
      key: 'prices',
      width: 80,
      align: 'center' as const,
      render: (_: any, offer: ProductOffer) => String(offer.prices?.length || 0).padStart(2, '0')
    },
    {
      title: 'Página de pagamento (Eduzz Checkout Elements)',
      key: 'link',
      render: (_: any, offer: ProductOffer, index: number) => (
        <div className='flex items-center gap-2'>
          <Icon.Link />
          <a
            className='text-content-body text-sm hover:underline'
            href={mountOfferLink(offer)}
            target='_blank'
            rel='noreferrer'
          >
            {mountOfferLink(offer)}
          </a>
          <Button
            type='text'
            id={`offer-copy-link-${index}`}
            icon={<Icon.Copy />}
            onClick={() => copyLinkToClipboard(mountOfferLink(offer))}
          />
        </div>
      )
    },
    {
      title: 'Visível para afiliados',
      key: 'affiliate',
      width: 180,
      align: 'center' as const,
      render: (_: any, offer: ProductOffer) => (offer.is_visible_affiliate ? 'Sim' : 'Não')
    },
    {
      title: 'Ações',
      key: 'actions',
      width: 75,
      fixed: 'right' as const,
      align: 'center' as const,
      render: (_: any, offer: ProductOffer, index: number) => (
        <ActionsMenu
          product={product}
          offer={offer}
          openConfirmModal={openConfirmModal}
          hasUnsavedChanges={hasUnsavedChanges}
        >
          <Button
            type='text'
            id={`offer-actions-${index}`}
            icon={<Icon.Ellipsis />}
            onClick={e => e.preventDefault()}
          />
        </ActionsMenu>
      )
    }
  ];

  return (
    <Table
      size='small'
      scroll={{ x: 1000 }}
      loading={offersQuery.isLoading}
      dataSource={offersQuery.data}
      columns={columns}
      rowKey='id'
      pagination={false}
      locale={{
        emptyText: offersQuery.isError ? 'Ocorreu um erro ao carregar as ofertas' : 'Nenhuma oferta cadastrada'
      }}
    />
  );
}

const List = React.memo(Component);

export { List };
